/**
 * Shared helpers for guest-image ("Gastbild") tokens in builder image blocks.
 *
 * An image block bound to an IMAGE-typed additional field stores
 * `{{additionalFields.<name>}}` in `props.imageUrls[lang]`. At real send time
 * the consumer/backend resolve the recipient's value (cid-inline / data URI);
 * in the FE the canvas + preview swap the token for the guest's presigned URL
 * (variable-replacer image pass) or the neutral placeholder below.
 */

/** Matches a WHOLE imageUrls value that is one additional-field token.
 *  Group 1 = the field name (tolerant charset — names may carry spaces/umlauts,
 *  mirroring VAR_TOKEN_RE). */
export const AF_IMAGE_TOKEN_RE = /^\{\{additionalFields\.([^{}]+)\}\}$/;

/** Field name inside a guest-image token, or null when `src` isn't one. */
export function parseAfImageToken(src: string | undefined | null): string | null {
  if (!src) return null;
  const m = AF_IMAGE_TOKEN_RE.exec(src.trim());
  return m ? m[1] : null;
}

/** Build the stored token for a field name. */
export function afImageToken(fieldName: string): string {
  return `{{additionalFields.${fieldName}}}`;
}

/**
 * Neutral inline-SVG placeholder (light grey tile with a photo glyph) shown
 * where a guest image will render — canvas (no guest) and previews whose
 * guest has no value. Self-contained data URI: no network, no theme tokens
 * (it renders inside the document/email canvas, which has its own colors).
 */
export const AF_IMAGE_PLACEHOLDER_DATA_URI: string =
  "data:image/svg+xml," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="240" height="180" viewBox="0 0 240 180">' +
      '<rect width="240" height="180" fill="#e5e7eb"/>' +
      '<path d="M60 130l40-44 30 32 20-20 30 32z" fill="#9ca3af"/>' +
      '<circle cx="88" cy="62" r="14" fill="#9ca3af"/>' +
      "</svg>",
  );
